import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { Dropdown as BSDropdown } from 'react-bootstrap';
import { SearchInput } from 'shared/ui/SearchInput/SearchInput';
import { LinkroleTreeNode, nodeCheckState } from './aiLinkroleFilter';
import './LinkroleFilterTree.scss';

interface LinkroleFilterTreeProps {
  forest: LinkroleTreeNode[];
  selected: string[];
  onToggle: (value: string) => void;
}

/** Keep nodes that match `q` (with their whole subtree) or that have a matching descendant. */
function pruneForest(forest: LinkroleTreeNode[], q: string): LinkroleTreeNode[] {
  if (!q) return forest;
  const lower = q.toLowerCase();
  const prune = (nodes: LinkroleTreeNode[]): LinkroleTreeNode[] =>
    nodes.reduce<LinkroleTreeNode[]>((acc, n) => {
      if (n.searchText.toLowerCase().includes(lower)) {
        acc.push(n); // self match — reveal the whole subtree
      } else {
        const children = prune(n.children);
        if (children.length) acc.push({ ...n, children });
      }
      return acc;
    }, []);
  return prune(forest);
}

const TreeRow: React.FC<{
  node: LinkroleTreeNode;
  depth: number;
  ancestorSelected: boolean;
  selectedSet: Set<string>;
  expanded: Set<string>;
  searching: boolean;
  onToggle: (value: string) => void;
  onExpand: (value: string) => void;
}> = ({ node, depth, ancestorSelected, selectedSet, expanded, searching, onToggle, onExpand }) => {
  const state = nodeCheckState(node, selectedSet, ancestorSelected);
  const checked = state === 'checked' || state === 'covered';
  const disabled = state === 'covered';
  const hasChildren = node.children.length > 0;
  const isOpen = searching || expanded.has(node.value);

  return (
    <>
      <div
        className="linkrole-tree__row"
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => hasChildren && onExpand(node.value)}
        role="treeitem"
        aria-selected={checked}
        aria-expanded={hasChildren ? isOpen : undefined}
      >
        <span className="linkrole-tree__caret" aria-hidden>
          {hasChildren ? <i className={`fa-solid fa-chevron-${isOpen ? 'down' : 'right'}`} /> : null}
        </span>
        <input
          type="checkbox"
          className="linkrole-tree__checkbox"
          checked={checked}
          disabled={disabled}
          ref={(el) => {
            if (el) el.indeterminate = state === 'indeterminate';
          }}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggle(node.value)}
          aria-label={node.label}
        />
        <span className="linkrole-tree__label" title={node.label}>
          {node.label}
        </span>
      </div>
      {hasChildren &&
        isOpen &&
        node.children.map((child) => (
          <TreeRow
            key={child.value}
            node={child}
            depth={depth + 1}
            ancestorSelected={ancestorSelected || selectedSet.has(node.value)}
            selectedSet={selectedSet}
            expanded={expanded}
            searching={searching}
            onToggle={onToggle}
            onExpand={onExpand}
          />
        ))}
    </>
  );
};

export const LinkroleFilterTree = ({ forest, selected, onToggle }: LinkroleFilterTreeProps) => {
  const { t } = useTranslation('tag');

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleForest = useMemo(() => pruneForest(forest, search.trim()), [forest, search]);
  const searching = search.trim().length > 0;

  const onExpand = (value: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });

  const label = selected.length ? `${selected.length} ${t('ai.linkrole.filter.count')}` : t('ai.linkrole.filter.all');

  return (
    <div className="ai-modal__linkrole-filter">
      <span className="ai-modal__linkrole-filter-label">{t('ai.linkrole.filter.label')}</span>
      {forest.length === 0 ? (
        <span className="ai-modal__linkrole-filter-empty">{t('ai.linkrole.filter.none')}</span>
      ) : (
        <BSDropdown
          show={isOpen}
          autoClose="outside"
          onToggle={(next) => {
            setIsOpen(next);
            if (!next) setSearch('');
          }}
        >
          <BSDropdown.Toggle variant="secondary" size="sm" className="linkrole-tree__toggle">
            {label}
          </BSDropdown.Toggle>
          <BSDropdown.Menu className="linkrole-tree__menu" renderOnMount popperConfig={{ strategy: 'fixed' }}>
            <div className="linkrole-tree__search" onClick={(e) => e.stopPropagation()}>
              <SearchInput searchInput={search} setSearchInput={setSearch} />
            </div>
            <div className="linkrole-tree__scroll" role="tree">
              {visibleForest.map((root) => (
                <TreeRow
                  key={root.value}
                  node={root}
                  depth={0}
                  ancestorSelected={false}
                  selectedSet={selectedSet}
                  expanded={expanded}
                  searching={searching}
                  onToggle={onToggle}
                  onExpand={onExpand}
                />
              ))}
            </div>
          </BSDropdown.Menu>
        </BSDropdown>
      )}
    </div>
  );
};
